import {
  SSO_PROVIDER,
  type SsoProviderId,
  type SsoCompletedMeta,
} from "@/components/SSOAuthButton/types";
import { decodeJwt } from "@/services/JwtService/JwtService";
import type { AppleIdTokenClaims } from "@/hooks/auth/useAppleAuth";

export interface SsoUserData {
  userId: string;
  email: string;
}

type SsoExtractorFunction = (payload: unknown, meta?: SsoCompletedMeta) => SsoUserData | null;

export class SsoUserDataExtractor {
  private static readonly extractors: Record<SsoProviderId, SsoExtractorFunction> = {
    [SSO_PROVIDER.APPLE]: this.extractAppleUserData,
    [SSO_PROVIDER.GOOGLE]: this.extractGoogleUserData,
    [SSO_PROVIDER.MICROSOFT]: this.extractMicrosoftUserData,
  };

  /**
   * Extracts user data from SSO provider response
   * @param provider - The SSO provider (Google, Apple)
   * @param payload - The SSO response payload
   * @param meta - Optional metadata (e.g., Apple ID token)
   * @returns User data or null if extraction fails
   */
  static extractUserData(
    provider: SsoProviderId,
    payload: unknown,
    meta?: SsoCompletedMeta
  ): SsoUserData | null {
    const extractor = this.extractors[provider];
    return extractor ? extractor(payload, meta) : null;
  }

  private static extractAppleUserData(
    payload: unknown,
    meta?: SsoCompletedMeta
  ): SsoUserData | null {
    const registerData = payload as { data?: { auth?: { token?: string } } };
    const appToken = registerData?.data?.auth?.token;
    const appleIdToken = meta?.registerSsoToken;

    if (!appToken || !appleIdToken) {
      return null;
    }

    const decoded = decodeJwt(appleIdToken);
    const claims = (decoded || {}) as AppleIdTokenClaims;

    return {
      userId: claims.sub ?? "unknown",
      email: claims.email ?? "unknown",
    };
  }

  private static extractGoogleUserData(payload: unknown): SsoUserData | null {
    const googleData = payload as { data?: { auth?: { token?: string } } };
    const token = googleData?.data?.auth?.token;

    if (!token) {
      return null;
    }

    const decoded = decodeJwt(token);

    return {
      userId: decoded?.id || "unknown",
      email: decoded?.email || "unknown",
    };
  }

  private static extractMicrosoftUserData(
    payload: unknown,
    meta?: SsoCompletedMeta
  ): SsoUserData | null {
    const registerData = payload as { data?: { auth?: { token?: string } } };
    const appToken = registerData?.data?.auth?.token;
    const microsoftIdToken = meta?.registerSsoToken;

    if (!appToken || !microsoftIdToken) {
      return null;
    }

    const decoded = decodeJwt(microsoftIdToken);
    const claims = (decoded || {}) as Record<string, any>;

    return {
      userId: claims.oid ?? claims.sub ?? "unknown",
      email: claims.upn ?? claims.email ?? "unknown",
    };
  }
}
